import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { STATION_TYPE_LABEL } from '@/config/brand';
import { useBookingAlarm } from '@/hooks/useBookingAlarm';
import { formatClock } from '@/lib/dates';
import { primeAlarm, startAlarm, stopAlarm } from '@/lib/alarm';

/**
 * Global new-booking alarm. When a booking comes in from the public site it
 * shows a banner and rings a chime that keeps repeating until an operator taps
 * "Got it". Mounted once inside the authenticated app shell.
 */
export function BookingAlarm() {
  const { pending, acknowledge } = useBookingAlarm();
  const ringing = pending.length > 0;

  // Unlock/keep the audio context warm on any interaction (browser autoplay).
  useEffect(() => {
    const prime = () => primeAlarm();
    window.addEventListener('pointerdown', prime);
    window.addEventListener('keydown', prime);
    return () => {
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
  }, []);

  useEffect(() => {
    if (ringing) startAlarm();
    else stopAlarm();
    return () => stopAlarm();
  }, [ringing]);

  if (!ringing) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-[100] flex justify-center p-3">
      <div className="w-full max-w-lg rounded-2xl border-2 border-brand bg-elevated p-4 shadow-2xl ring-4 ring-brand/25">
        <div className="flex items-center gap-2">
          <span className="animate-pulse text-2xl" aria-hidden>
            🔔
          </span>
          <h2 className="text-base font-semibold text-content">
            {pending.length === 1 ? 'New online booking' : `${pending.length} new online bookings`}
          </h2>
        </div>

        <ul className="mt-3 space-y-1.5">
          {pending.slice(0, 5).map((b) => (
            <li
              key={b.id}
              className="rounded-lg border border-line bg-overlay px-3 py-2 text-sm text-content"
            >
              <span className="font-medium">{b.customerName}</span>
              <span className="text-muted">
                {' · '}
                {b.stationType ? STATION_TYPE_LABEL[b.stationType] : 'Table'}
                {' · '}
                {formatClock(b.startAt)} · {b.durationMinutes} min
              </span>
            </li>
          ))}
          {pending.length > 5 && (
            <li className="text-xs text-muted">+{pending.length - 5} more</li>
          )}
        </ul>

        <Button fullWidth size="lg" className="mt-3" onClick={acknowledge}>
          Got it — stop alarm
        </Button>
      </div>
    </div>
  );
}
